import { Collapsible } from '@base-ui/react/collapsible';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React, { FC, PropsWithChildren } from 'react';

import MenuItemCore, { MenuItemCoreBaseProps } from '../Menu/MenuItemCore';

// Forwarding a caller's generic `as` through MenuItemCore's own polymorphic
// typing is not provable by the compiler. The public NavListItemProps typing
// has already validated the rest props against the caller's element type, so
// this internal alias widens MenuItemCore to accept them.
const PolymorphicMenuItemCore = MenuItemCore as FC<
  MenuItemCoreBaseProps & {
    as: React.ElementType;
    children?: React.ReactNode;
  } & Record<string, unknown>
>;

export interface NavListProps extends PropsWithChildren {
  className?: string;
}

/**
 * A vertical list of navigation links, with optional collapsible groups.
 *
 * ## When to use
 *
 * - A page or section navigation list: a settings nav, a sidebar.
 * - Grouped navigation where sections expand and collapse.
 *
 * ## When to choose another component
 *
 * - **Action menus and dropdowns** — use `MenuItemCore` / `ContextMenuRoot`.
 *   NavList rows are links to places, not actions.
 * - **Collapsing content, not navigation** — use `Accordion`.
 * - **Switching views within one page** — use `Tabs`.
 *
 * ## Composition rules
 *
 * - A `NavList.Group` contains a `NavList.GroupHeader` then a
 *   `NavList.GroupPanel`.
 * - The header is one row: its label link's click target stretches across the
 *   whole row, and the chevron the header renders when given a `triggerLabel`
 *   sits above it, so pressing the chevron toggles the group without
 *   following the link.
 * - When expansion is driven externally, pass a controlled `open` to `NavList.Group`.
 * - **Active state**: render items that should highlight the current page
 *   with `as={NavLink}` (react-router). `NavLink` sets `aria-current="page"`
 *   on the active link, and the row styles itself from that attribute — there
 *   is no active prop to manage. Use plain `Link` for rows that never show an
 *   active state, e.g. a group header that only ever navigates onward.
 *
 * ✅ Correct:
 *
 * ```tsx
 * <NavList>
 *   <NavList.Group defaultOpen>
 *     <NavList.GroupHeader
 *       as={Link}
 *       to="/settings/tax"
 *       triggerLabel="Toggle Tax section"
 *     >
 *       Tax
 *     </NavList.GroupHeader>
 *     <NavList.GroupPanel>
 *       <NavList.Item as={NavLink} to="/settings/tax/vat">VAT</NavList.Item>
 *     </NavList.GroupPanel>
 *   </NavList.Group>
 * </NavList>
 * ```
 *
 * ❌ Wrong — a plain `Link` never receives `aria-current`, so the row cannot
 * show it is the current page:
 *
 * ```tsx
 * <NavList.Item as={Link} to="/settings/tax/vat">VAT</NavList.Item>
 * ```
 *
 * ## Accessibility
 *
 * - NavList renders a plain `<ul>`. When it is a page navigation landmark,
 *   wrap it in a `<nav>` with an `aria-label`.
 * - The active row is conveyed by `aria-current="page"`, set by `NavLink`.
 * - `triggerLabel` names the icon-only chevron for screen readers; its
 *   expanded state is conveyed by `aria-expanded`, wired automatically.
 */
const NavList: FC<NavListProps> = (props: NavListProps) => {
  const { className, children } = props;
  return (
    <ul className={clstx('m-0 flex list-none flex-col gap-1 p-0', className)}>
      {children}
    </ul>
  );
};

type NavListItemOwnProps<E extends React.ElementType> = {
  /** The element to render the row as, e.g. a router `Link` or `NavLink`. */
  as?: E;
  iconName?: TIconName;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

export type NavListItemProps<E extends React.ElementType = 'a'> =
  NavListItemOwnProps<E> &
    Omit<React.ComponentPropsWithoutRef<E>, keyof NavListItemOwnProps<E>>;

const Item = <E extends React.ElementType = 'a'>(
  props: NavListItemProps<E>
) => {
  const { as, iconName, disabled, className, children, ...rest } = props;
  return (
    <li>
      <PolymorphicMenuItemCore
        as={as ?? 'a'}
        iconName={iconName}
        disabled={disabled}
        wrapperClassName={className}
        {...rest}
      >
        {/* A span, not a bare string: MenuItemCore renders string children as
            a <p>, which a legacy global `p` rule shrinks and pushes
            off-centre. */}
        <span>{children}</span>
      </PolymorphicMenuItemCore>
    </li>
  );
};

export interface NavListGroupProps extends PropsWithChildren {
  /** Controlled open state. Use `defaultOpen` for uncontrolled groups. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

const Group: FC<NavListGroupProps> = (props: NavListGroupProps) => {
  const { open, defaultOpen, onOpenChange, className, children } = props;
  return (
    <Collapsible.Root
      render={<li />}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange && ((isOpen) => onOpenChange(isOpen))}
      className={className}
    >
      {children}
    </Collapsible.Root>
  );
};

type NavListGroupHeaderOwnProps<E extends React.ElementType> = {
  /** The element to render the label link as, e.g. a router `Link` or `NavLink`. */
  as?: E;
  iconName?: TIconName;
  /**
   * Screen-reader name for the chevron toggle (e.g. "Toggle Tax section").
   * When omitted, no toggle renders — for groups whose expansion is driven
   * externally, e.g. a search that force-expands.
   */
  triggerLabel?: string;
  className?: string;
  children: React.ReactNode;
};

export type NavListGroupHeaderProps<E extends React.ElementType = 'a'> =
  NavListGroupHeaderOwnProps<E> &
    Omit<
      React.ComponentPropsWithoutRef<E>,
      keyof NavListGroupHeaderOwnProps<E>
    >;

const GroupHeader = <E extends React.ElementType = 'a'>(
  props: NavListGroupHeaderProps<E>
) => {
  const { as, iconName, triggerLabel, className, children, ...rest } = props;
  const LabelLink: React.ElementType = as ?? 'a';
  return (
    <MenuItemCore
      as="div"
      iconName={iconName}
      wrapperClassName={clstx('relative', className)}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1">
        {/* stretched-link makes the whole row the link's click target; the
            trigger sits above it at z-20, so it toggles without navigating. */}
        <LabelLink
          className="stretched-link min-w-0 flex-1 font-normal text-inherit no-underline"
          {...rest}
        >
          <span>{children}</span>
        </LabelLink>
        {triggerLabel !== undefined && (
          <Collapsible.Trigger
            aria-label={triggerLabel}
            className="group relative z-20 flex shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-1 text-inherit"
          >
            <UntitledIcon
              name="chevron-down"
              className="size-4 transition-transform duration-300 group-data-[panel-open]:rotate-180"
            />
          </Collapsible.Trigger>
        )}
      </span>
    </MenuItemCore>
  );
};

export interface NavListGroupPanelProps extends PropsWithChildren {
  className?: string;
}

const GroupPanel: FC<NavListGroupPanelProps> = (
  props: NavListGroupPanelProps
) => {
  const { className, children } = props;
  return (
    // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Base UI collapsible panel height; limits the transition to these properties
    <Collapsible.Panel className="h-[var(--collapsible-panel-height)] overflow-hidden transition-[height] duration-200 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0">
      <ul
        className={clstx(
          'm-0 ml-5 flex list-none flex-col gap-1 border-l-2 border-gray-100 p-0 pt-1 pl-3',
          className
        )}
      >
        {children}
      </ul>
    </Collapsible.Panel>
  );
};

export default Object.assign(NavList, {
  Item,
  Group,
  GroupHeader,
  GroupPanel,
});
